import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, increment, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import { COLLECTIONS } from "./collections";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";

/**
 * WaitlistService
 * 
 * Gerencia a lista de espera de turmas.
 * Desnormaliza dados do membro e da turma para leitura eficiente.
 * 
 * Regras de Negócio:
 * - Cada membro pode estar na lista de espera de múltiplas turmas.
 * - A posição na fila é calculada pela ordem de solicitação (requestedAt).
 * - Ao adicionar: incrementa `waitlistCount` na turma e adiciona classId em `member.waitlistClassIds`.
 * - Ao remover: decrementa `waitlistCount` na turma e remove classId de `member.waitlistClassIds`.
 * - Ao promover (matricular): remove da lista e chama o fluxo normal de matrícula.
 */
const WaitlistService = {

    /**
     * Adiciona um membro à lista de espera de uma turma.
     * 
     * @param {string} idTenant
     * @param {Object} user - Usuário autenticado
     * @param {Object} options - { idMember, memberName, memberPhone, memberEmail, memberPhotoUrl, idClass, classData }
     * @returns {Object} { entryId, position }
     */
    addToWaitlist: async (idTenant, user, options) => {
        const { idMember, memberName, memberPhone, memberEmail, memberPhotoUrl, idClass, classData } = options;

        if (!idTenant || !idMember || !idClass) {
            throw new Error("Parâmetros incompletos para adicionar à lista de espera.");
        }

        try {
            const waitlistRef = collection(db, "tenants", idTenant, COLLECTIONS.WAITLIST_ENTRIES);

            // Verificar se já está na lista desta turma
            const existingQuery = query(
                waitlistRef,
                where("idMember", "==", idMember),
                where("idClass", "==", idClass),
                where("status", "==", "waiting")
            );
            const existingSnap = await getDocs(existingQuery);
            if (!existingSnap.empty) {
                throw new Error("Este aluno já está na lista de espera desta turma.");
            }

            // Calcular posição na fila (próxima posição disponível)
            const classWaitlistQuery = query(
                waitlistRef,
                where("idClass", "==", idClass),
                where("status", "==", "waiting")
            );
            const classWaitlistSnap = await getDocs(classWaitlistQuery);
            const position = classWaitlistSnap.size + 1;

            const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
            const memberSnap = await getDoc(memberRef);
            let mData = {};
            if (memberSnap.exists()) {
                mData = memberSnap.data();
                const currentWaitlistIds = mData.waitlistClassIds || [];
                if (!currentWaitlistIds.includes(idClass)) {
                    await updateDoc(memberRef, {
                        waitlistClassIds: [...currentWaitlistIds, idClass],
                        updatedAt: serverTimestamp()
                    });
                }
            }

            // Criar entrada na lista de espera
            const entryData = {
                idMember,
                memberName: memberName || mData.fullName || "",
                memberPhone: memberPhone || mData.phone || "",
                memberEmail: memberEmail || mData.email || "",
                memberPhotoUrl: memberPhotoUrl || mData.photoUrl || "",
                idClass,
                activityName: classData?.activityName || "",
                weekdayLabel: classData?.weekdayLabel || "",
                startTime: classData?.startTime || "",
                endTime: classData?.endTime || "",
                idEmployee: classData?.idEmployee || "",
                employeeName: classData?.employeeName || "",
                employeeEmail: classData?.employeeEmail || "",
                employeePhone: classData?.employeePhone || "",
                employeePhotoUrl: classData?.employeePhotoUrl || "",
                position,
                status: "waiting",
                requestedAt: serverTimestamp(),
                notifiedAt: null,
                enrolledAt: null,
                createdBy: {
                    uid: user?.uid || "system",
                    name: user?.displayName || user?.name || user?.email || "Sistema"
                }
            };

            const newEntry = await addDoc(waitlistRef, entryData);

            // Incrementar contador na turma
            const classRef = doc(db, "tenants", idTenant, COLLECTIONS.CLASSES, idClass);
            await updateDoc(classRef, {
                waitlistCount: increment(1),
                updatedAt: serverTimestamp()
            });

            // Auditoria
            AuditService.log({
                idTenant,
                user,
                module: AUDIT_MODULES.ENROLLMENTS,
                action: AUDIT_ACTIONS.CREATE,
                entityId: idMember,
                entityName: memberName,
                description: `Adicionou "${memberName}" à lista de espera da turma ${classData?.activityName || idClass} (posição #${position}).`,
                details: {
                    tipo: "waitlist",
                    turma: classData?.activityName || idClass,
                    posicao: position,
                    entryId: newEntry.id
                }
            });

            return { entryId: newEntry.id, position };
        } catch (error) {
            console.error("[WaitlistService] Erro ao adicionar à lista de espera:", error);
            throw error;
        }
    },

    /**
     * Remove um membro da lista de espera.
     * 
     * @param {string} idTenant
     * @param {Object} user
     * @param {string} entryId - ID do documento de waitlistEntries
     * @param {string} idClass - ID da turma (para decrementar contador)
     * @param {string} idMember - ID do membro (para atualizar waitlistClassIds)
     */
    removeFromWaitlist: async (idTenant, user, entryId, idClass, idMember) => {
        if (!idTenant || !entryId) {
            throw new Error("Parâmetros incompletos para remover da lista de espera.");
        }

        try {
            const entryRef = doc(db, "tenants", idTenant, COLLECTIONS.WAITLIST_ENTRIES, entryId);
            const entrySnap = await getDoc(entryRef);
            
            if (!entrySnap.exists()) {
                throw new Error("Entrada na lista de espera não encontrada.");
            }

            const entryData = entrySnap.data();
            const resolvedIdClass = idClass || entryData.idClass;
            const resolvedIdMember = idMember || entryData.idMember;

            // Deletar o documento
            await deleteDoc(entryRef);

            // Decrementar contador na turma
            if (resolvedIdClass) {
                const classRef = doc(db, "tenants", idTenant, COLLECTIONS.CLASSES, resolvedIdClass);
                await updateDoc(classRef, {
                    waitlistCount: increment(-1),
                    updatedAt: serverTimestamp()
                });
            }

            // Remover do array waitlistClassIds do membro
            if (resolvedIdMember && resolvedIdClass) {
                const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, resolvedIdMember);
                const memberSnap = await getDoc(memberRef);
                if (memberSnap.exists()) {
                    const currentIds = memberSnap.data().waitlistClassIds || [];
                    // Verificar se ainda tem outras entradas para essa turma
                    const remainingQuery = query(
                        collection(db, "tenants", idTenant, COLLECTIONS.WAITLIST_ENTRIES),
                        where("idMember", "==", resolvedIdMember),
                        where("idClass", "==", resolvedIdClass),
                        where("status", "==", "waiting")
                    );
                    const remainingSnap = await getDocs(remainingQuery);
                    if (remainingSnap.empty) {
                        await updateDoc(memberRef, {
                            waitlistClassIds: currentIds.filter(id => id !== resolvedIdClass),
                            updatedAt: serverTimestamp()
                        });
                    }
                }
            }

            // Recalcular posições dos que ficaram na fila
            await WaitlistService._recalculatePositions(idTenant, resolvedIdClass);

            // Auditoria
            AuditService.log({
                idTenant,
                user,
                module: AUDIT_MODULES.ENROLLMENTS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: resolvedIdMember,
                entityName: entryData.memberName || resolvedIdMember,
                description: `Removeu "${entryData.memberName || 'Membro'}" da lista de espera da turma ${entryData.activityName || resolvedIdClass}.`,
                details: { tipo: "waitlist_removal", entryId }
            });

            return true;
        } catch (error) {
            console.error("[WaitlistService] Erro ao remover da lista de espera:", error);
            throw error;
        }
    },

    /**
     * Lista de espera de uma turma, ordenada por posição.
     */
    getClassWaitlist: async (idTenant, idClass) => {
        if (!idTenant || !idClass) return [];

        try {
            const waitlistRef = collection(db, "tenants", idTenant, COLLECTIONS.WAITLIST_ENTRIES);
            const q = query(
                waitlistRef,
                where("idClass", "==", idClass),
                where("status", "==", "waiting"),
                orderBy("position", "asc")
            );
            const snap = await getDocs(q);
            return snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (error) {
            console.error("[WaitlistService] Erro ao buscar lista de espera da turma:", error);
            return [];
        }
    },

    /**
     * Lista de espera de um membro específico (para exibir no perfil).
     */
    getMemberWaitlist: async (idTenant, idMember) => {
        if (!idTenant || !idMember) return [];

        try {
            const waitlistRef = collection(db, "tenants", idTenant, COLLECTIONS.WAITLIST_ENTRIES);
            const q = query(
                waitlistRef,
                where("idMember", "==", idMember),
                where("status", "==", "waiting")
            );
            const snap = await getDocs(q);
            return snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (error) {
            console.error("[WaitlistService] Erro ao buscar lista de espera do membro:", error);
            return [];
        }
    },

    /**
     * Busca o primeiro da fila de uma turma.
     */
    getFirstInLine: async (idTenant, idClass) => {
        if (!idTenant || !idClass) return null;

        try {
            const waitlistRef = collection(db, "tenants", idTenant, COLLECTIONS.WAITLIST_ENTRIES);
            const q = query(
                waitlistRef,
                where("idClass", "==", idClass),
                where("status", "==", "waiting"),
                orderBy("position", "asc")
            );
            const snap = await getDocs(q);
            if (snap.empty) return null;
            const firstDoc = snap.docs[0];
            return { id: firstDoc.id, ...firstDoc.data() };
        } catch (error) {
            console.error("[WaitlistService] Erro ao buscar primeiro da fila:", error);
            return null;
        }
    },

    /**
     * Recalcula as posições da fila após uma remoção.
     * @private
     */
    _recalculatePositions: async (idTenant, idClass) => {
        if (!idTenant || !idClass) return;

        try {
            const waitlistRef = collection(db, "tenants", idTenant, COLLECTIONS.WAITLIST_ENTRIES);
            const q = query(
                waitlistRef,
                where("idClass", "==", idClass),
                where("status", "==", "waiting"),
                orderBy("position", "asc")
            );
            const snap = await getDocs(q);
            
            let newPosition = 1;
            for (const docSnap of snap.docs) {
                if (docSnap.data().position !== newPosition) {
                    await updateDoc(docSnap.ref, { position: newPosition });
                }
                newPosition++;
            }
        } catch (error) {
            console.error("[WaitlistService] Erro ao recalcular posições:", error);
        }
    },
};

export default WaitlistService;
